import React, { ErrorInfo, ReactNode } from 'react';
import { Box, Button, Container, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary Component
 * Catches errors in child components and displays a user-friendly error page
 * WCAG 2.1 AA compliant with proper ARIA labels and keyboard navigation
 */
class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // Log to console for development
    console.error('Error caught by boundary:', error, errorInfo);
    
    // In production, you would send this to an error tracking service (Sentry)
    // Sentry.captureException(error, { contexts: { react: errorInfo } });
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
    // Optionally reload the page
    window.location.href = '/';
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Container maxWidth="sm">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '100vh',
              gap: 3,
              padding: 2,
            }}
            role="alert"
            aria-live="assertive"
          >
            <ErrorOutlineIcon
              sx={{
                fontSize: 80,
                color: 'error.main',
              }}
              aria-hidden="true"
            />
            <Typography variant="h3" component="h1" sx={{ textAlign: 'center' }}>
              Oops! Something went wrong
            </Typography>
            <Typography
              variant="body1"
              color="textSecondary"
              sx={{ textAlign: 'center' }}
            >
              We encountered an unexpected error. Our team has been notified and is
              working to fix it. Please try refreshing the page or come back in a
              few moments.
            </Typography>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Box
                component="pre"
                sx={{
                  width: '100%',
                  padding: 2,
                  backgroundColor: 'background.default',
                  borderRadius: 1,
                  overflow: 'auto',
                  textAlign: 'left',
                  fontSize: '0.75rem',
                  color: 'error.main',
                  fontFamily: 'monospace',
                }}
              >
                {this.state.error.toString()}
              </Box>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={this.handleReset}
              size="large"
            >
              Go Back Home
            </Button>
          </Box>
        </Container>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
